import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { slideMetrics, DEFAULT_FONT, DEFAULT_FONT_PT, SLIDE_W_PT, SLIDE_H_PT } from "@/lib/slides";

const SAMPLE = [
  "O Jesus, meek and humble of heart,",
  "hear me and teach me to pray.",
  "Can you read this line from the back row?",
];

export function ProjectorCheck({ font = DEFAULT_FONT, fontSize = DEFAULT_FONT_PT, onClose }) {
  const stageRef = useRef(null);
  const [scale, setScale] = useState(1);
  const metrics = slideMetrics(fontSize);

  useEffect(() => {
    const measure = () => {
      const el = stageRef.current;
      if (!el) return;
      setScale(Math.min(el.clientWidth / SLIDE_W_PT, el.clientHeight / SLIDE_H_PT));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const pt = (v) => `${v * scale}px`;
  const corner = "absolute h-10 w-10 border-white/70";

  return (
    <div className="fixed inset-0 z-[200] bg-black text-white" style={{ fontFamily: font }} data-testid="projector-check-overlay">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 rounded-full opacity-25 hover:opacity-100 hover:bg-white/10 transition-opacity"
        title="Close projector check (Esc)"
        data-testid="projector-check-close-button"
      >
        <X className="h-4 w-4" />
      </button>

      <div className={`${corner} top-4 left-4 border-t-2 border-l-2`} />
      <div className={`${corner} top-4 right-4 border-t-2 border-r-2`} />
      <div className={`${corner} bottom-4 left-4 border-b-2 border-l-2`} />
      <div className={`${corner} bottom-4 right-4 border-b-2 border-r-2`} />

      <div ref={stageRef} className="absolute inset-0">
        <div className="absolute left-1/2 top-1/2" style={{ width: pt(SLIDE_W_PT), height: pt(SLIDE_H_PT), transform: "translate(-50%, -50%)" }}>
          <div className="absolute" style={{ left: pt(72), right: pt(72), top: pt(64), bottom: pt(130) }}>
            <div className="h-full flex flex-col justify-center">
              {SAMPLE.map((line, i) => (
                <p key={i} className="text-white" style={{ fontSize: pt(metrics.bodyPt), lineHeight: 1.25, marginBottom: pt(6) }}>
                  {line}
                </p>
              ))}
              <p className="text-white" style={{ fontSize: pt(metrics.labelPt), lineHeight: 1.25, marginTop: pt(18), opacity: 0.85 }}>
                Verse 1 · label size
              </p>
            </div>
          </div>

          <div className="absolute flex" style={{ left: pt(48), right: pt(48), bottom: pt(62), height: pt(22) }} data-testid="projector-check-greyscale">
            {[0, 10, 25, 40, 55, 70, 85, 100].map((v) => (
              <div key={v} className="flex-1" style={{ backgroundColor: `rgb(${Math.round(v * 2.55)},${Math.round(v * 2.55)},${Math.round(v * 2.55)})` }} />
            ))}
          </div>

          <div className="absolute flex items-center justify-between text-white/50 uppercase tracking-widest" style={{ left: pt(48), right: pt(48), bottom: pt(26), fontSize: pt(13) }}>
            <span>Projector check · {font} {metrics.bodyPt} pt</span>
            <span>Esc to exit</span>
          </div>
        </div>
      </div>
    </div>
  );
}
